import React, { useContext, useEffect, useState } from "react";
import { AppRouterContext } from "../../AppRouter";
import { RefundRequestDetailsCard } from "../../components/RefundRequestDetailsCard";
import {
  deleteFailedComponentsFromRefundRequest,
  deleteRefundRequest,
  fetchActivityLogs,
  fetchRefundRequest,
  refundRefundRequest,
  updateChecklistItem,
  updateComponent,
  voidRefundRequest,
} from "../../actions/refunds";
import { AuthContext } from "../../contexts/Auth";
import {
  ActivityLog,
  ChecklistItemType,
  RefundRequest,
  RefundRequestComponentStatus,
  RefundRequestComponentType,
} from "../../../common/types";
import { Screen } from "../../components/Screen";
import { RefundRequestComponentsTableCard } from "../../components/RefundRequestComponentsTableCard";
import { RefundRequestChecklistCard } from "../../components/RefundRequestChecklistCard";
import "./refund-request-screen.scss";
import { getComponentTypeLabel } from "../../utils";
import { ConfigContext } from "../../contexts/Config";
import { Breadcrumbs } from "@glamcorner/components";
import { isChecklistLocked } from "../../../common/utils";
import { Heading } from "../../components/Heading";
import { findCheckListItem } from "../../../common/models/RefundRequest/findChecklistItem";
import { ActivityLogComponentCard } from "../../components/ActivityLogComponentCard";
import { Breadcrumb } from "@glamcorner/components/lib/core/components/Breadcrumbs";

export const RefundRequestScreen: React.FC = () => {
  const { routerProps } = useContext(AppRouterContext);
  const { config } = useContext(ConfigContext);
  const { id } = routerProps.match.params;
  const { fetchToken, userId, userName, userRoles } = useContext(AuthContext);
  const [refundRequest, setRefundRequest] = useState<RefundRequest | undefined>(undefined);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[] | undefined>(undefined);

  const updateRefundRequest = async () => {
    const token = await fetchToken();
    setRefundRequest(await fetchRefundRequest(token, id));
  };
  const updateActivityLogs = async () => {
    const token = await fetchToken();
    setActivityLogs(await fetchActivityLogs(token, id));
  };

  useEffect(() => {
    updateRefundRequest();
  }, []);

  useEffect(() => {
    updateActivityLogs();
  }, [refundRequest]);

  const statusOnChange = async ({
    type,
    status,
  }: {
    type: RefundRequestComponentType;
    status: RefundRequestComponentStatus;
  }) => {
    const confirmMsg = `Are you sure you want to update the status of ${getComponentTypeLabel(type)} to ${status}`;
    if (!confirm(confirmMsg)) {
      return;
    }

    const token = await fetchToken();
    const newRefundRequest = await updateComponent({
      token,
      type,
      id,
      status,
      userId,
      userName,
    });

    setRefundRequest(newRefundRequest);
  };

  const isChecklistItemChecked = (type: ChecklistItemType): boolean => {
    if (!refundRequest) {
      return false;
    }

    const checklistItem = findCheckListItem(refundRequest, type);

    if (!checklistItem) {
      return false;
    }

    return !!checklistItem.checkedAt;
  };

  const checklistItemOnClick = async (type: ChecklistItemType) => {
    const token = await fetchToken();
    const isChecked = !isChecklistItemChecked(type);
    try {
      const newRefundRequest = await updateChecklistItem({
        token,
        type,
        id,
        isChecked,
        userId,
        userName,
      });

      setRefundRequest(newRefundRequest);
    } catch (e) {
      alert(e);
    }
  };

  const buildBreadcrumbs = (): Breadcrumb[] => {
    if (!refundRequest) {
      return [];
    }

    return [{ label: "Refund Requests", href: "/#/" }, { label: refundRequest.id, href: null }];
  };

  const allChecklistItemsComplete = !!(refundRequest && !refundRequest.checklistItems.find(ci => !ci.checkedAt));

  const onDeleteHandler = async (): Promise<void> => {
    if (
      !refundRequest ||
      !confirm(
        "Only delete refund requests when they are duplicates!\n\n" +
          "You should usually use Void instead,\n" +
          "so we keep a record that the refund was rejected.\n\n" +
          "Are you sure you want to delete this refund request\ninstead of voiding it?"
      )
    ) {
      return;
    }

    const token = await fetchToken();
    const newRefundRequest = await deleteRefundRequest({ token, id: refundRequest.id, userId, userName });
    setRefundRequest(newRefundRequest);
  };

  const onVoidHandler: (data: { reason: string; note: string }) => Promise<void> = async ({
    reason,
    note,
  }): Promise<void> => {
    if (!refundRequest) {
      return;
    }
    const token = await fetchToken();
    const newRefundRequest = await voidRefundRequest({
      token,
      id: refundRequest.id,
      userId,
      userName,
      voidReason: reason,
      voidNote: note,
    });
    setRefundRequest(newRefundRequest);
  };

  const onRefundHandler = async () => {
    if (!refundRequest || !confirm("Are you sure you want to immediately schedule this refund request?")) {
      return;
    }

    const token = await fetchToken();
    const newRefundRequest = await refundRefundRequest({ token, id: refundRequest.id, userId, userName });
    setRefundRequest(newRefundRequest);
  };

  const onDeleteFailedComponentsHandler: () => Promise<void> = async () => {
    if (!refundRequest || !confirm("Are you sure you want to delete failed components for this refund request?")) {
      return;
    }

    const token = await fetchToken();
    const newRefundRequest = await deleteFailedComponentsFromRefundRequest({ token, id: refundRequest.id, userId, userName });
    setRefundRequest(newRefundRequest);
  };

  return (
    <Screen className="c-refund-request-screen">
      <Heading />
      <div className="c-refund-request-screen__breadcrumbs-container">
        <Breadcrumbs className="l-m-hidden" breadcrumbs={buildBreadcrumbs()} />
      </div>

      {refundRequest && (
        <div className="c-refund-request-screen__content">
          <div className="c-refund-request-screen__left">
            <div className="c-refund-request-screen__details">
              <RefundRequestDetailsCard
                refundRequest={refundRequest}
                inventoryUrl={config.inventoryUrl}
                onClickDelete={onDeleteHandler}
                onClickVoid={onVoidHandler}
                onRefundHandler={onRefundHandler}
                onDeleteFailedComponents={onDeleteFailedComponentsHandler}
              />
            </div>
          </div>
          <div className="c-refund-request-screen__middle">
            <div className="c-refund-request-screen__checklist">
              <RefundRequestChecklistCard
                checklistItems={refundRequest.checklistItems}
                itemOnClick={checklistItemOnClick}
                isDisabled={isChecklistLocked(refundRequest.status)}
                userRoles={userRoles}
              />
            </div>
            <div className="c-refund-request-screen__components">
              <RefundRequestComponentsTableCard
                components={refundRequest.components}
                statusOnChange={statusOnChange}
                allChecklistItemsComplete={allChecklistItemsComplete}
              />
            </div>
          </div>
          {activityLogs && (
            <div className="c-refund-request-screen__right">
              <div className="c-refund-request-screen__activity-log">
                <ActivityLogComponentCard activityLogs={activityLogs} />
              </div>
            </div>
          )}
        </div>
      )}
    </Screen>
  );
};
